import type { ComponentType } from "react";

/**
 * Frontmatter schema for blog posts. The MDX plugin (configured in
 * vite.config.ts via remark-mdx-frontmatter) exposes the YAML frontmatter
 * block as a named `frontmatter` export from each .mdx module.
 *
 * `image` is an optional absolute path under public/ (e.g. /hero.jpg) used
 * as the OpenGraph + Twitter share image for the post. When absent the
 * route falls back to a site-wide default.
 */
export interface BlogPostFrontmatter {
  date: string;
  description: string;
  image?: string;
  slug?: string;
  title: string;
}

export interface BlogPost {
  Component: ComponentType<{ components?: Record<string, unknown> }>;
  date: string;
  description: string;
  image?: string;
  slug: string;
  title: string;
}

interface BlogModule {
  default: ComponentType<{ components?: Record<string, unknown> }>;
  frontmatter: BlogPostFrontmatter;
}

// Eagerly import all blog posts at build time. The MDX plugin compiles each
// .mdx file into a React component plus a `frontmatter` named export, so we
// can build a sorted index without any runtime parsing.
const modules = import.meta.glob<BlogModule>("../content/blog/*.mdx", {
  eager: true,
});

function deriveSlugFromPath(path: string): string {
  const match = path.match(/\/([^/]+)\.mdx$/);
  return match?.[1] ?? path;
}

const allPosts: BlogPost[] = Object.entries(modules)
  .map(([path, mod]) => {
    const fm = mod.frontmatter;
    if (!fm?.title || !fm?.date) {
      throw new Error(
        `Blog post at ${path} is missing required frontmatter (title, date).`,
      );
    }
    return {
      Component: mod.default,
      date: fm.date,
      description: fm.description ?? "",
      image: fm.image,
      slug: fm.slug ?? deriveSlugFromPath(path),
      title: fm.title,
    };
  })
  .sort((a, b) => b.date.localeCompare(a.date));

export const blogPosts: ReadonlyArray<BlogPost> = allPosts;

export function getPostBySlug(slug: string): BlogPost | undefined {
  return allPosts.find((post) => post.slug === slug);
}
